import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import NotificationService, { 
  PushNotification, 
  NotificationType, 
  NotificationPermissions,
  NotificationPriority 
} from '../../services/notificationService';

// 状态接口
interface NotificationState {
  notifications: PushNotification[];
  permissions: NotificationPermissions | null;
  unreadCount: number;
  businessNotificationCount: number; // 新增：业务通知计数
  loading: {
    init: boolean;
    permissions: boolean;
    sending: boolean;
  };
  error: string | null;
  lastUpdate: number;
}

// 初始状态
const initialState: NotificationState = {
  notifications: [],
  permissions: null,
  unreadCount: 0,
  businessNotificationCount: 0, // 新增：业务通知计数初始值
  loading: {
    init: false,
    permissions: false,
    sending: false
  },
  error: null,
  lastUpdate: 0
};

// 异步操作

// 初始化通知服务
export const initializeNotifications = createAsyncThunk(
  'notifications/initialize',
  async (_, { rejectWithValue, getState }) => {
    try {
      const service = NotificationService.getInstance();
      await service.initialize();
      
      // 获取当前 Redux state 中的未读计数，而不是从 service 获取
      const state = getState() as any;
      const currentUnreadCount = state.notifications.unreadCount;
      
      return {
        notifications: service.getNotifications(),
        permissions: service.getPermissions(),
        unreadCount: currentUnreadCount // 使用 Redux store 中的计数
      };
    } catch (error) {
      console.error('[NotificationSlice] 初始化失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '初始化通知服务失败');
    }
  }
);

// 检查权限
export const checkNotificationPermissions = createAsyncThunk(
  'notifications/checkPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      const hasPermission = await service.checkPermissions();
      
      return {
        hasPermission,
        permissions: service.getPermissions()
      };
    } catch (error) {
      console.error('[NotificationSlice] 检查权限失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '检查权限失败');
    }
  }
);

// 请求特定类型通知权限
export const requestNotificationPermission = createAsyncThunk(
  'notifications/requestPermission',
  async (type: NotificationType, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      const granted = await service.requestNotificationPermission(type);
      
      return {
        type,
        granted,
        permissions: service.getPermissions()
      };
    } catch (error) {
      console.error('[NotificationSlice] 请求权限失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '请求权限失败');
    }
  }
);

// 发送本地通知
export const sendLocalNotification = createAsyncThunk(
  'notifications/sendLocal',
  async (notification: Omit<PushNotification, 'id' | 'timestamp' | 'read'>, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.sendLocalNotification(notification);
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 发送通知失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '发送通知失败');
    }
  }
);

// 重新加载通知（用于切换账号或手动刷新）
export const reloadNotifications = createAsyncThunk(
  'notifications/reload',
  async (_, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.reloadNotifications();
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 重新加载通知失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '重新加载通知失败');
    }
  }
);

// 标记为已读
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.markAsRead(notificationId);
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 标记已读失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '标记已读失败');
    }
  }
);

// 标记所有为已读
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.markAllAsRead();
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 批量标记已读失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '批量标记已读失败');
    }
  }
);

// 删除通知
export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.deleteNotification(notificationId);
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 删除通知失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '删除通知失败');
    }
  }
);

// 清空所有通知
export const clearAllNotifications = createAsyncThunk(
  'notifications/clearAll',
  async (_, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.clearAllNotifications();
      
      return {
        notifications: service.getNotifications(),
        unreadCount: service.getUnreadCount()
      };
    } catch (error) {
      console.error('[NotificationSlice] 清空通知失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '清空通知失败');
    }
  }
);

// 更新通知设置
export const updateNotificationSettings = createAsyncThunk(
  'notifications/updateSettings',
  async ({ type, enabled }: { type: NotificationType; enabled: boolean }, { rejectWithValue }) => {
    try {
      const service = NotificationService.getInstance();
      await service.updateNotificationSettings(type, enabled);
      
      return {
        type,
        enabled,
        permissions: service.getPermissions()
      };
    } catch (error) {
      console.error('[NotificationSlice] 更新设置失败:', error);
      return rejectWithValue(error instanceof Error ? error.message : '更新设置失败');
    }
  }
);

// 创建slice
const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // 同步更新通知列表（用于实时监听）
    updateNotifications: (state, action: PayloadAction<PushNotification[]>) => {
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(n => !n.read).length;
      state.lastUpdate = Date.now();
    },

    // 清除错误
    clearError: (state) => {
      state.error = null;
    },

    // 重置加载状态
    resetLoading: (state) => {
      state.loading = {
        init: false,
        permissions: false,
        sending: false
      };
    },

    // 添加单个通知（用于实时接收）
    addNotification: (state, action: PayloadAction<PushNotification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
      state.lastUpdate = Date.now();
    },

    // 本地标记已读（优化UI响应）
    markAsReadLocal: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n: PushNotification) => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // 清除所有未读计数（当用户查看通知页面时）
    clearUnreadCount: (state) => {
      console.log('🧹 clearUnreadCount reducer被调用，当前计数:', state.unreadCount);
      state.unreadCount = 0;
      console.log('🧹 清除后计数:', state.unreadCount);
    },

    // 更新业务通知计数
    updateBusinessNotificationCount: (state, action: PayloadAction<number>) => {
      console.log('🔔 更新业务通知计数:', action.payload);
      state.businessNotificationCount = action.payload;
    },

    // 设置业务通知计数（不覆盖，而是设置基础值）
    setBaseBusinessNotificationCount: (state, action: PayloadAction<number>) => {
      console.log('🔔 设置基础业务通知计数:', action.payload);
      state.businessNotificationCount = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 初始化通知服务
    builder
      .addCase(initializeNotifications.pending, (state) => {
        state.loading.init = true;
        state.error = null;
      })
      .addCase(initializeNotifications.fulfilled, (state, action) => {
        state.loading.init = false;
        state.notifications = action.payload.notifications;
        state.permissions = action.payload.permissions;
        state.unreadCount = action.payload.unreadCount;
        state.lastUpdate = Date.now();
      })
      .addCase(initializeNotifications.rejected, (state, action) => {
        state.loading.init = false;
        state.error = action.payload as string;
      });

    // 检查权限
    builder
      .addCase(checkNotificationPermissions.pending, (state) => {
        state.loading.permissions = true;
        state.error = null;
      })
      .addCase(checkNotificationPermissions.fulfilled, (state, action) => {
        state.loading.permissions = false;
        state.permissions = action.payload.permissions;
      })
      .addCase(checkNotificationPermissions.rejected, (state, action) => {
        state.loading.permissions = false;
        state.error = action.payload as string;
      });

    // 请求权限
    builder
      .addCase(requestNotificationPermission.pending, (state) => {
        state.loading.permissions = true;
        state.error = null;
      })
      .addCase(requestNotificationPermission.fulfilled, (state, action) => {
        state.loading.permissions = false;
        state.permissions = action.payload.permissions;
      })
      .addCase(requestNotificationPermission.rejected, (state, action) => {
        state.loading.permissions = false;
        state.error = action.payload as string;
      });

    // 发送本地通知
    builder
      .addCase(sendLocalNotification.pending, (state) => {
        state.loading.sending = true;
        state.error = null;
      })
      .addCase(sendLocalNotification.fulfilled, (state, action) => {
        state.loading.sending = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
        state.lastUpdate = Date.now();
      })
      .addCase(sendLocalNotification.rejected, (state, action) => {
        state.loading.sending = false;
        state.error = action.payload as string;
      });

    // 重新加载通知
    builder
      .addCase(reloadNotifications.pending, (state) => {
        state.loading.notifications = true;
        state.error = null;
      })
      .addCase(reloadNotifications.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
        state.lastUpdate = Date.now();
      })
      .addCase(reloadNotifications.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error = action.payload as string;
      });

    // 标记已读
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 标记所有已读
    builder
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 删除通知
    builder
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 清空所有通知
    builder
      .addCase(clearAllNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(clearAllNotifications.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // 更新设置
    builder
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.permissions = action.payload.permissions;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

// 导出actions
export const {
  updateNotifications,
  clearError,
  resetLoading,
  addNotification,
  markAsReadLocal,
  clearUnreadCount,
  updateBusinessNotificationCount,
  setBaseBusinessNotificationCount
} = notificationsSlice.actions;

// 选择器
export const selectNotifications = (state: { notifications: NotificationState }) => state.notifications.notifications;
export const selectUnreadCount = (state: { notifications: NotificationState }) => state.notifications.unreadCount;
export const selectBusinessNotificationCount = (state: { notifications: NotificationState }) => state.notifications.businessNotificationCount;
export const selectNotificationPermissions = (state: { notifications: NotificationState }) => state.notifications.permissions;
export const selectNotificationLoading = (state: { notifications: NotificationState }) => state.notifications.loading;
export const selectNotificationError = (state: { notifications: NotificationState }) => state.notifications.error;

// 按类型选择通知
export const selectNotificationsByType = (type: NotificationType) => (state: { notifications: NotificationState }) =>
  state.notifications.notifications.filter(n => n.type === type);

// 按模块选择通知
export const selectNotificationsByModule = (moduleType: string) => (state: { notifications: NotificationState }) =>
  state.notifications.notifications.filter(n => n.moduleType === moduleType);

// 选择未读通知
export const selectUnreadNotifications = (state: { notifications: NotificationState }) =>
  state.notifications.notifications.filter(n => !n.read);

export default notificationsSlice.reducer;
